/**
 * Canonical list of CAD / BIM file extensions that go through
 * Autodesk Platform Services (APS) for in-browser viewing.
 *
 * Lives outside `lib/actions/uploads.ts` so the browser-direct
 * multipart upload helper + the upload button can detect "is
 * this a CAD/BIM file?" client-side without dragging a server
 * action into the bundle.
 *
 * Stays in sync with the legacy `CAD_BIM_EXTENSIONS` constant
 * in `lib/actions/uploads.ts` (which still uses it for the
 * Supabase-fallback path on non-CAD files).
 */

import { compareBimCadFileOrder } from "@/lib/virtualspace/bim-file-sort";

export const CAD_BIM_EXTENSIONS: ReadonlySet<string> = new Set([
  // Autodesk-native
  "rvt",
  "dwg",
  "dxf",
  "nwd",
  "nwc",
  "f3d",
  "dgn",
  // Open / interchange
  "ifc",
  "stp",
  "step",
  "igs",
  "iges",
  "obj",
  "fbx",
  "skp",
  "3dm",
  // SOLIDWORKS
  "sldprt",
  "sldasm",
  "prt",
  "asm",
  // CATIA
  "catpart",
  "catproduct",
]);

/**
 * Returns the lowercase extension (without the dot) for a file
 * name, or empty string if no extension.
 */
export function fileExtension(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  if (dot < 0 || dot === fileName.length - 1) return "";
  return fileName.slice(dot + 1).toLowerCase();
}

export function isCadBimFile(fileName: string): boolean {
  return CAD_BIM_EXTENSIONS.has(fileExtension(fileName));
}

/**
 * Federation-loading priority for CAD/BIM files. Higher number
 * = loaded first into the Autodesk Viewer scene. The first
 * model loaded establishes the camera frame, so we want the
 * file most likely to give a complete view of the project to
 * be primary.
 *
 * Priority ladder (rationale):
 *   - RVT / RFA  → Revit master + family files. The fullest
 *                  BIM model; if it exists, it should anchor
 *                  the scene.
 *   - IFC        → Cross-platform BIM exchange. Usually a
 *                  full federated model.
 *   - NWD / NWC  → Navisworks deliverables, often already a
 *                  federation of multiple disciplines.
 *   - DGN        → Bentley MicroStation. Complete 3D when
 *                  delivered.
 *   - 3DM / 3DS  → Rhino / 3ds Max. Full 3D meshes.
 *   - SKP        → SketchUp models.
 *   - DWFX / DWF → Autodesk's design web format; may be 2D
 *                  sheets or 3D scenes.
 *   - DWG / DXF  → AutoCAD drawings, very often 2D plans /
 *                  elevations. Last because they typically
 *                  collapse to the same origin (overlap each
 *                  other) and don't establish a useful 3D
 *                  camera frame on their own.
 *
 * Ties broken by alphabetical filename order so the result is
 * stable across server renders.
 */
export function cadBimLoadPriority(fileName: string | null | undefined): number {
  if (!fileName) return 0;
  const ext = fileExtension(fileName);
  switch (ext) {
    case "rvt":
      return 100;
    case "rfa":
      return 95;
    case "ifc":
      return 90;
    case "nwd":
      return 80;
    case "nwc":
      return 70;
    case "dgn":
      return 65;
    case "3dm":
    case "3ds":
      return 60;
    case "skp":
      return 55;
    case "dwfx":
    case "dwf":
      return 50;
    case "stp":
    case "step":
    case "igs":
    case "iges":
      return 45;
    case "f3d":
      return 40;
    case "obj":
    case "fbx":
      return 35;
    case "dwg":
      return 20;
    case "dxf":
      return 15;
    case "pdf":
      return 10;
    default:
      return 5;
  }
}

/**
 * Pick the single CAD/BIM filename that should auto-start a Model
 * Derivative job in a multi-file upload / archive extract.
 *
 * Matches Studio / viewer federation order: highest
 * `cadBimLoadPriority`, then `compareBimCadFileOrder` (floor/sheet
 * heuristics) — so `modelList[0]` and the auto-translate target agree.
 *
 * Returns null when `fileNames` is empty. Callers usually pre-filter
 * with `isCadBimFile`.
 */
export function pickPrimaryCadBimFileName(
  fileNames: ReadonlyArray<string>,
): string | null {
  if (fileNames.length === 0) return null;
  const sorted = [...fileNames].sort((a, b) => {
    const pa = cadBimLoadPriority(a);
    const pb = cadBimLoadPriority(b);
    if (pa !== pb) return pb - pa;
    return compareBimCadFileOrder({ fileName: a }, { fileName: b });
  });
  return sorted[0] ?? null;
}

/**
 * Index of the federation-primary CAD/BIM file in `files` (by
 * `name`). Prefer this over name equality when a batch may contain
 * duplicate basenames — only one index auto-translates.
 */
export function pickPrimaryCadBimFileIndex(
  files: ReadonlyArray<{ name: string }>,
): number {
  const cadIndexes: number[] = [];
  const cadNames: string[] = [];
  for (let i = 0; i < files.length; i++) {
    if (!isCadBimFile(files[i].name)) continue;
    cadIndexes.push(i);
    cadNames.push(files[i].name);
  }
  if (cadIndexes.length === 0) return -1;
  const primaryName = pickPrimaryCadBimFileName(cadNames);
  if (primaryName == null) return -1;
  // First matching index among CAD rows (stable with sort ties).
  const amongCad = cadNames.indexOf(primaryName);
  return amongCad >= 0 ? cadIndexes[amongCad]! : -1;
}
